import type { Ref } from "vue";
import type { ShortcutJs } from "@/generated/models";
import { createInjectionState } from "@vueuse/core";
import { useContextOrThrow } from "ls/composables/useContextOrThrow";
import { computed, onUnmounted, readonly, ref, watch } from "vue";
import { equalsIgnoringCase } from "@/common/lib";
import { ShortcutType } from "@/generated/models";
import { onDocumentBackupEvent } from "./backups/documentBackupEvents";
import { onRestoreBackupStarted } from "./bridge/collaboration";
import { onLoadingFailed } from "./bridge/projectState";

interface FileContext {
  synced: boolean;
  loadingFailed: boolean;
  restoringBackup: boolean;
}

const filesContexts = ref(new Map<string, FileContext>());

const [useProvideCurrentFileContext, useCurrentFileContextFn] = createInjectionState((file: Ref<ShortcutJs>) => {
  const shortcut = computed(() => file.value.Shortcut);
  const type = computed(() => file.value.ShortcutType);
  const isDoc = computed(() => type.value === ShortcutType.Document);
  const isWhiteboard = computed(() => type.value === ShortcutType.Whiteboard);
  const isDesign = computed(() => type.value === ShortcutType.Design);

  const productName = computed(() => {
    if (isDoc.value) return "Axure documents";
    if (isDesign.value) return "Axure designs";
    if (isWhiteboard.value) return "Axure whiteboards";
    throw new Error("Unsupported file type");
  });

  const fileContext = computed(() => {
    const context = filesContexts.value.get(shortcut.value);
    if (context) {
      return context;
    }

    const newContext = {
      synced: false,
      loadingFailed: false,
      restoringBackup: false,
    };
    filesContexts.value.set(shortcut.value, newContext);
    return filesContexts.value.get(shortcut.value)!;
  });

  const projectLoadingFailed = computed(() => fileContext.value.loadingFailed);
  const restoringBackup = computed(() => fileContext.value.restoringBackup);

  onLoadingFailed(() => {
    fileContext.value.loadingFailed = true;
  });

  onRestoreBackupStarted(() => {
    fileContext.value.restoringBackup = true;
  });

  onDocumentBackupEvent(payload => {
    const payloadShortcut = payload.event === "restore" ? payload.request.shortcut : payload.shortcut;
    if (!equalsIgnoringCase(shortcut.value, payloadShortcut)) return;

    if (payload.event === "started") {
      fileContext.value.restoringBackup = true;
    } else if (payload.event === "finished") {
      fileContext.value.restoringBackup = false;

      // can probably be less aggressive
      window.location.reload();
    }
  });

  const synced = computed({
    get() {
      return fileContext.value.synced;
    },
    set(value) {
      fileContext.value.synced = value;
    },
  });

  watch(shortcut, (_, previous) => {
    filesContexts.value.delete(previous);
  });

  onUnmounted(() => {
    filesContexts.value.clear();
  });

  return {
    file: readonly(file),
    shortcut,
    type,
    isDoc,
    isWhiteboard,
    isDesign,
    productName,
    projectLoadingFailed,
    restoringBackup,
    synced,
  };
});

export { useProvideCurrentFileContext };
export const useCurrentFileContextUnsafe = useCurrentFileContextFn;
export const useCurrentFileContext = useContextOrThrow(useCurrentFileContextFn);
